import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TimeRangeContext } from '../../context/TimeRangeContext';
import api from '../../api/Base';
import { DATA_LOADING, DATA_LOADED, DATA_FAILED } from '../../component/Constants';
import MultiInstanceTimeSeriesChart from '../../component/instance/chart/MultiInstanceTimeSeriesChart';
import HostTimeStatistic from '../../types/host/HostTimeStatistic';
import { GroupDetailSlideoutProps, TimeSeriesDataPoint, InstanceSeriesData, HostSeriesData } from './types';
import WidgetCard from "../../component/instance/widget";
import ConditionalRender from "../../helpers/ConditionalRender";

const GroupDetailSlideout: React.FC<GroupDetailSlideoutProps> = ({ groupName, instances, onClose, applyPeriod, hosts}) => {
    // Remove useNavigate
    const { timeRange, getTimeRangeQueryString } = useContext(TimeRangeContext);
    const [loadingInstanceCharts, setLoadingInstanceCharts] = useState(DATA_LOADING); // Renamed for clarity
    const [totalTimeSeries, setTotalTimeSeries] = useState<InstanceSeriesData[]>([]);
    const [executionsTimeSeries, setExecutionsTimeSeries] = useState<InstanceSeriesData[]>([]);
    const [loadingHostCharts, setLoadingHostCharts] = useState(DATA_LOADING); // State for host charts
    const [cpuTimeSeries, setCpuTimeSeries] = useState<HostSeriesData[]>([]); // State for CPU data
    const [ioTimeSeries, setIoTimeSeries] = useState<HostSeriesData[]>([]); // State for Disk I/O data
    // Add state for card collapse
    const [isTotalTimeCollapsed, setIsTotalTimeCollapsed] = useState(false); // Default expanded
    const [isExecutionsCollapsed, setIsExecutionsCollapsed] = useState(false); // Default expanded
    const [isCpuCollapsed, setIsCpuCollapsed] = useState(false); // Default expanded for CPU
    const [isIoCollapsed, setIsIoCollapsed] = useState(false); // Default expanded for I/O

    // Simplified dropdown state
    const [isOpen, setIsOpen] = useState(false);
    const dropdownMenuRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
    };

    const handleClickOutside = (event: any) => {
        if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const instanceIds = useMemo(() => instances.map(inst => inst.id), [instances]);
    // Extract unique hosts (assuming hostId is now available on InstanceTarget)
    // const uniqueHosts = useMemo(() => {
    //     const hostsMap = new Map<number, string>();
    //     instances.forEach(inst => {
    //         // Ensure hostId exists and is a number before adding
    //         if (inst.hostId !== undefined && typeof inst.hostId === 'number' && !hostsMap.has(inst.hostId)) {
    //             hostsMap.set(inst.hostId, inst.host || `Host ${inst.hostId}`); // Use inst.host as name, fallback if needed
    //         }
    //     });
    //     // Convert map to array of objects
    //     return Array.from(hostsMap, ([hostId, hostName]) => ({ hostId, hostName }));
    // }, [instances]);

    const uniqueHosts = useMemo(() => {
        const hostsMap = new Map<number, string>();

        instances.forEach(inst => {
            const hostIds = Array.isArray(inst.hostId) ? inst.hostId : [inst.hostId];

            console.log('hostIds', hostIds)
            hostIds.forEach(id => {
                const  getHost = hosts.find(h => h.id === id);
                console.log('hosts', hosts)

                if (typeof id === 'number' && !hostsMap.has(id)) {
                    hostsMap.set(id, getHost?.name || `Host ${id}`);
                }
            });
        });

        return Array.from(hostsMap, ([hostId, hostName]) => ({ hostId, hostName }));
    }, [instances]);



    // --- Calculate Aggregated Stats ---
    const aggregatedStats = useMemo(() => {
        let totalTime = 0;
        let totalExecutions = 0;
        let totalAlerts = 0;
        let totalChanges = 0;
        let totalCustom = 0;

        instances.forEach(inst => {
            totalTime += inst.metrics?.totalTime ?? 0;
            totalExecutions += inst.metrics?.totalExecutions ?? 0;
            totalAlerts += inst.eventCounts?.alert ?? 0;
            totalChanges += inst.eventCounts?.change ?? 0;
            totalCustom += inst.eventCounts?.custom ?? 0;
        });

        const averageTime = totalExecutions > 0 ? totalTime / totalExecutions : 0;

        return {
            totalTime,
            totalExecutions,
            averageTime,
            totalAlerts,
            totalChanges,
            totalCustom,
        };
    }, [instances]);

    // --- Fetch Time-Series Data for Charts ---
    // --- Fetch Instance Time-Series Data ---
    useEffect(() => {
        // Only run if instanceIds are available and timeRange is set
        if (!instanceIds || instanceIds.length === 0 || !timeRange) {
            setLoadingInstanceCharts(DATA_LOADED); // Set to loaded if no instances to fetch for
            setTotalTimeSeries([]);
            setExecutionsTimeSeries([]);
            return;
        }
        ;

        const fetchInstanceData = async () => {
            setLoadingInstanceCharts(DATA_LOADING);
            const timeQueryString = getTimeRangeQueryString();

            const fetchInstanceSeries = async (instanceId: number, statistic: 'waittime' | 'executions'): Promise<TimeSeriesDataPoint[]> => {
                let url = '';
                let valueMapper: (item: any) => number;

                if (statistic === 'executions') {
                    // Use statistic/time endpoint for executions, interval=0, map 'sum'
                    url = `statistic/time?statistic=${statistic}&id=${instanceId}&${timeQueryString}&interval=0&limit=501`;
                    valueMapper = (item) => item.sum ?? 0;
                } else { // Default to 'waittime' logic (original)
                    // Use activity/time endpoint for waittime, interval=300, map 'waittime'
                    const interval = 300; // 5 minutes
                    url = `activity/time?statistic=${statistic}&id=${instanceId}&${timeQueryString}&interval=${interval}`;
                    valueMapper = (item) => item[statistic] ?? 0; // Access item.waittime
                }

                try {
                    const response = await api.get<any[]>(url);
                    return response.data.map(item => ({
                        timeslice: item.timeslice,
                        value: valueMapper(item)
                    })) || [];
                } catch (error) {
                    console.error(`Error fetching ${statistic} data for instance ${instanceId} from ${url}:`, error);
                    return [];
                }
            };

            try {
                const promisesTotalTime = instances.map(inst =>
                        fetchInstanceSeries(inst.id, 'waittime').then(data => ({ instanceId: inst.id, instanceName: inst.name, data }))
                );
                const promisesExecutions = instances.map(inst =>
                        fetchInstanceSeries(inst.id, 'executions').then(data => ({ instanceId: inst.id, instanceName: inst.name, data }))
                );

                const resultsTotalTime = await Promise.all(promisesTotalTime);
                const resultsExecutions = await Promise.all(promisesExecutions);

                setTotalTimeSeries(resultsTotalTime);
                setExecutionsTimeSeries(resultsExecutions);
                setLoadingInstanceCharts(DATA_LOADED);

            } catch (error) {
                console.error('Error fetching instance time series data for group:', error);
                setTotalTimeSeries([]);
                setExecutionsTimeSeries([]);
                setLoadingInstanceCharts(DATA_FAILED);
            }
        };

        void fetchInstanceData();

    }, [instanceIds, timeRange, getTimeRangeQueryString, instances]); // Keep dependencies

    // --- Fetch Host Time-Series Data ---
    useEffect(() => {
        // Only run if uniqueHosts are available and timeRange is set
        if (!uniqueHosts || uniqueHosts.length === 0 || !timeRange) {
            setLoadingHostCharts(DATA_LOADED); // Set to loaded if no hosts to fetch for
            setCpuTimeSeries([]);
            setIoTimeSeries([]);
            return;
        }

        const fetchHostData = async () => {
            setLoadingHostCharts(DATA_LOADING);
            const timeQueryString = getTimeRangeQueryString();

            // Helper function to fetch data for one host and statistic
            const fetchHostSeries = async (hostId: number, statistic: 'cpuutilisation' | 'ioutilisation'): Promise<TimeSeriesDataPoint[]> => {
                // URL encode metric name - though these specific ones might not need it
                const encodedStatistic = encodeURIComponent(statistic);
                const url = `host/statistic/time?id=${hostId}&statistic=${encodedStatistic}&${timeQueryString}&limit=501`;
                // Use avg based on DiskReadWriteTimeChart example
                const valueMapper = (item: HostTimeStatistic) => item.avg ?? 0;

                try {
                    // Assuming response structure { data: HostTimeStatistic[] } like DiskReadWriteTimeChart
                    const response = await api.get<{ data: HostTimeStatistic[] }>(url);
                    // Check data exists and is array before mapping
                    if (response.data && Array.isArray(response.data)) {
                        return response.data.map(item => ({
                            // Convert timeslice using getConvertedTimeToUTC if needed, or use raw string if chart handles it
                            // Assuming MultiInstanceTimeSeriesChart expects ISO string like instance data
                            timeslice: item.timeslice,
                            value: valueMapper(item)
                        })) || [];
                    } else {
                        console.warn(`No data or invalid format for host ${hostId}, metric ${statistic}:`, response.data);
                        return [];
                    }
                } catch (error) {
                    console.error(`Error fetching ${statistic} data for host ${hostId} from ${url}:`, error);
                    return []; // Return empty array on error for this host/metric
                }
            };

            try {
                const promisesCpu = uniqueHosts.map(host =>
                        fetchHostSeries(host.hostId, 'cpuutilisation').then(data => ({ hostId: host.hostId, hostName: host.hostName, data }))
                );
                const promisesIo = uniqueHosts.map(host =>
                        fetchHostSeries(host.hostId, 'ioutilisation').then(data => ({ hostId: host.hostId, hostName: host.hostName, data }))
                );

                const resultsCpu = await Promise.all(promisesCpu);
                const resultsIo = await Promise.all(promisesIo);

                // Filter out hosts with no data if necessary, or let chart handle empty series
                setCpuTimeSeries(resultsCpu); //.filter(series => series.data.length > 0));
                setIoTimeSeries(resultsIo); //.filter(series => series.data.length > 0));
                setLoadingHostCharts(DATA_LOADED);

            } catch (error) {
                console.error('Error fetching host time series data for group:', error);
                setCpuTimeSeries([]);
                setIoTimeSeries([]);
                setLoadingHostCharts(DATA_FAILED);
            }
        };

        void fetchHostData();

    }, [uniqueHosts, timeRange, getTimeRangeQueryString]); // Depend on uniqueHosts and timeRange

    return (
            // Add instance-detail-slideout class here to apply the specific summary card styles
            // Changed p-3 to pt-3 to remove horizontal padding from main container
            <div className="group-detail-content instance-detail-slideout pt-3">
                {/* Header - Match InstanceDetailSlideout structure */}
                {/* Added px-3 to header row to maintain padding */}
                <div className="row justify-content-between align-items-center mb-3 px-3">
                    {/* Left Side: Header Info */}
                    <div className="col">
                        <div className="header-content d-flex align-items-center"> {/* Keep inner flex for alignment */}
                            <h5 className="mb-0">Group: {groupName} ({instances.length} {instances.length > 1 ? 'Instances' : 'Instance'})</h5>
                        </div>
                    </div>
                    {/* Right Side: Control Buttons */}
                    <div className="col-auto">
                        <div className="control-buttons d-flex align-items-center"> {/* Keep inner flex for alignment */}
                            {/* Instance Dropdown */}
                            <div className="dropdown">
                                <button
                                        className="btn btn-outline-secondary btn-sm dropdown-toggle me-2 btn-dropdown dropdown-toggle"
                                        type="button"
                                        onClick={toggleDropdown}
                                        aria-expanded={isOpen}
                                >
                                    <i className="fal fa-database fa-fw me-1"></i>
                                    View Instance ({instances.length})
                                </button>
                                <div id='dropdown-menu' 
                                    className={`dropdown-menu dropdown-menu-end dropdown-menu-scroll ${isOpen ? "show" : ""}`}
                                    ref={dropdownMenuRef} 
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ transform: 'translate3d(-104px, 0, 0)' }}
                                >
                                    <ul className="col">
                                        {instances.map(inst => (
                                            <li key={inst.id} onClick={() => setIsOpen(false)}>
                                                <Link
                                                        className="dropdown-item"
                                                        to={`/instances/${inst.id}/activity?${getTimeRangeQueryString()}`}
                                                >
                                                    {inst.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Close Button - Using Bootstrap */}
                            <button
                                    type="button"
                                    className="btn-close custom-slideout-close-button" // Use Bootstrap's standard close button style
                                    aria-label="Close"
                                    onClick={onClose} // Call the passed-in handler
                            ></button>
                            {/* No icon needed inside btn-close */}
                        </div>
                    </div>
                </div>
                {/* Add a divider line */}
                {/* Divider is now full width as parent has no horizontal padding */}
                {/* Using only inline style, removed header-divider class */}
                <div
                        className="mb-3" // Keep margin bottom
                        style={{ borderBottom: '1px solid #dee2e6', width: '100%', display: 'block' }}
                ></div>

                {/* Aggregated Stats */}
                {/* Reduced margin bottom from mb-4 to mb-2 */}
                {/* Added px-3 to maintain horizontal padding */}
                <div className="row g-2 mb-0 px-3 row-cols-1 row-cols-sm-2 row-cols-xl-4 flexlist">
                    <WidgetCard
                        name="Total Time"
                        type='time'
                        iconClassName="time"
                        icon="fa-stopwatch"
                        value={aggregatedStats.totalTime}
                        previous={0}
                        cardTooltipText="The total time spent"
                    />

                    <WidgetCard
                        name="Executions"
                        iconClassName="executions"
                        icon="fa-chart-line"
                        value={aggregatedStats.totalExecutions}
                        previous={0}
                        cardTooltipText="The total number of executions"
                    />

                    <WidgetCard
                        name="Average Time"
                        icon="fa-clock"
                        type='time'
                        iconClassName="averagetime"
                        value={aggregatedStats.averageTime}
                        previous={0}
                        cardTooltipText="The average time per execution"
                    />

                    <WidgetCard
                        name="Events"
                        events={{
                            alerts: aggregatedStats.totalAlerts,
                            changes: aggregatedStats.totalChanges,
                            custom: aggregatedStats.totalCustom
                        }}
                    />
                </div>

                {/* Charts Section - Wrapped in row/col */}
                <div className="row g-2 px-3"> {/* Add row with padding/gutter */}
                    <div className="col-md-6"> {/* Explicitly set column width for medium+ screens */}
                        {/* Total Time Card */}
                        <div className="card mb-4"> {/* Card itself */}
                            {/* Apply InstanceDetailSlideout header style */}
                            <div className="card-header">
                                <i className="fal fa-stopwatch fa-fw" aria-hidden="true"></i> {/* Icon */}
                                {' '} {/* Space */}
                                Total Time per Instance {/* Title (no fw-bold) */}
                                {/* Collapse Toggle Icon */}
                                <i
                                        className="collapse-toggle" // Use the specific class
                                        role="button"
                                        onClick={() => setIsTotalTimeCollapsed(!isTotalTimeCollapsed)}
                                        data-bs-toggle="collapse"
                                        data-bs-target="#groupTotalTimeCollapse"
                                        aria-expanded={!isTotalTimeCollapsed}
                                        aria-controls="groupTotalTimeCollapse"
                                ></i>
                            </div>
                            <div id="groupTotalTimeCollapse" className={`collapse ${isTotalTimeCollapsed ? '' : 'show'}`}>
                                <div className="card-body px-3"> {/* Added px-3 like InstanceDetailSlideout */}
                                    <ConditionalRender if={loadingInstanceCharts === DATA_LOADING}>
                                        <div className="w-100 text-center time-chart-center text-muted mt-3">
                                            <div className="loader spinner chartSpinner"></div>
                                            <p>Loading data...</p>
                                        </div>
                                    </ConditionalRender>
                                    {loadingInstanceCharts === DATA_FAILED &&
                                        <div className="text-center text-danger p-3"><i className="fal fa-exclamation-triangle me-2"></i>Failed to load chart data.</div>}
                                    {loadingInstanceCharts === DATA_LOADED && totalTimeSeries.length > 0 && (
                                            <MultiInstanceTimeSeriesChart
                                                    // Adapt HostSeriesData to what MultiInstanceTimeSeriesChart expects if needed
                                                    // Assuming it can take { instanceId: number, instanceName: string, data: TimeSeriesDataPoint[] }
                                                    // If not, map here: seriesData={totalTimeSeries.map(d => ({ instanceId: d.instanceId, instanceName: d.instanceName, data: d.data }))}
                                                    seriesData={totalTimeSeries}
                                                    yAxisLabel="Total Time"
                                                    valueSuffix="ms"
                                                    chartType="column" // Change to column for stacked bar
                                                    applyPeriod={applyPeriod} // Pass down applyPeriod
                                            />
                                    )}
                                    {loadingInstanceCharts === DATA_LOADED && totalTimeSeries.length === 0 && (
                                            <div className="text-center text-muted p-3">No time data available for this group.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Close col for first card */}
                    <div className="col-md-6"> {/* Explicitly set column width for medium+ screens */}
                        {/* Executions Card */}
                        <div className="card mb-4"> {/* Card itself */}
                            {/* Apply InstanceDetailSlideout header style */}
                            <div className="card-header">
                                <i className="fal fa-chart-line fa-fw" aria-hidden="true"></i> {/* Icon */}
                                {' '} {/* Space */}
                                Executions per Instance {/* Title (no fw-bold) */}
                                {/* Collapse Toggle Icon */}
                                <i
                                        className="collapse-toggle" // Use the specific class
                                        role="button"
                                        onClick={() => setIsExecutionsCollapsed(!isExecutionsCollapsed)}
                                        data-bs-toggle="collapse"
                                        data-bs-target="#groupExecutionsCollapse"
                                        aria-expanded={!isExecutionsCollapsed}
                                        aria-controls="groupExecutionsCollapse"
                                ></i>
                            </div>
                            <div id="groupExecutionsCollapse" className={`collapse ${isExecutionsCollapsed ? '' : 'show'}`}>
                                <div className="card-body px-3"> {/* Added px-3 like InstanceDetailSlideout */}
                                    <ConditionalRender if={loadingInstanceCharts === DATA_LOADING}>
                                        <div className="w-100 text-center time-chart-center text-muted mt-3">
                                            <div className="loader spinner chartSpinner"></div>
                                            <p>Loading data...</p>
                                        </div>
                                    </ConditionalRender>
                                    {loadingInstanceCharts === DATA_FAILED &&
                                        <div className="text-center text-danger p-3"><i className="fal fa-exclamation-triangle me-2"></i>Failed to load chart data.</div>}
                                    {loadingInstanceCharts === DATA_LOADED && executionsTimeSeries.length > 0 && (
                                            <MultiInstanceTimeSeriesChart
                                                    // Adapt HostSeriesData if needed
                                                    seriesData={executionsTimeSeries}
                                                    yAxisLabel="Executions"
                                                    valueSuffix="" // No suffix for executions count
                                                    chartType="spline"
                                                    applyPeriod={applyPeriod} // Pass down applyPeriod
                                            />
                                    )}
                                    {loadingInstanceCharts === DATA_LOADED && executionsTimeSeries.length === 0 && (
                                            <div className="text-center text-muted p-3">No execution data available for this group.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Host Charts Section --- */}
                <div className="row g-2 px-3">
                        <>
                            <div className="col-md-6">
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <i className="fal fa-microchip fa-fw" aria-hidden="true"></i>
                                        {' '}
                                        CPU Utilisation per Host
                                        <i
                                            className="collapse-toggle"
                                            role="button"
                                            onClick={() => setIsCpuCollapsed(!isCpuCollapsed)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#groupCpuCollapse"
                                            aria-expanded={!isCpuCollapsed}
                                            aria-controls="groupCpuCollapse"
                                        ></i>
                                    </div>
                                    <div id="groupCpuCollapse" className={`collapse ${isCpuCollapsed ? '' : 'show'}`}>
                                        <div className="card-body px-3">
                                            <ConditionalRender if={loadingHostCharts === DATA_LOADING}>
                                                <div className="w-100 text-center time-chart-center text-muted mt-3">
                                                    <div className="loader spinner chartSpinner"></div>
                                                    <p>Loading data...</p>
                                                </div>
                                            </ConditionalRender>
                                            {loadingHostCharts === DATA_FAILED &&
                                                <div className="text-center text-danger p-3"><i className="fal fa-exclamation-triangle me-2"></i>Failed to load chart data.</div>}
                                            {loadingHostCharts === DATA_LOADED && cpuTimeSeries.length > 0 && (
                                                <MultiInstanceTimeSeriesChart
                                                    seriesData={cpuTimeSeries.map(d => ({ instanceId: d.hostId, instanceName: d.hostName, data: d.data }))}
                                                    yAxisLabel="CPU (%)"
                                                    valueSuffix="%"
                                                    chartType="spline"
                                                    applyPeriod={applyPeriod}
                                                />
                                            )}
                                            {loadingHostCharts === DATA_LOADED && cpuTimeSeries.length === 0 && (
                                                <div className="text-center text-muted p-3">No CPU utilisation data available for hosts in this group.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <i className="fal fa-hdd fa-fw" aria-hidden="true"></i>
                                        {' '}
                                        Disk I/O Utilisation per Host
                                        <i
                                            className="collapse-toggle"
                                            role="button"
                                            onClick={() => setIsIoCollapsed(!isIoCollapsed)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#groupIoCollapse"
                                            aria-expanded={!isIoCollapsed}
                                            aria-controls="groupIoCollapse"
                                        ></i>
                                    </div>
                                    <div id="groupIoCollapse" className={`collapse ${isIoCollapsed ? '' : 'show'}`}>
                                        <div className="card-body px-3">
                                            <ConditionalRender if={loadingHostCharts === DATA_LOADING}>
                                                <div className="w-100 text-center time-chart-center text-muted mt-3">
                                                    <div className="loader spinner chartSpinner"></div>
                                                    <p>Loading data...</p>
                                                </div>
                                            </ConditionalRender>
                                            {loadingHostCharts === DATA_FAILED &&
                                                <div className="text-center text-danger p-3"><i className="fal fa-exclamation-triangle me-2"></i>Failed to load chart data.</div>}
                                            {loadingHostCharts === DATA_LOADED && ioTimeSeries.length > 0 && (
                                                <MultiInstanceTimeSeriesChart
                                                    seriesData={ioTimeSeries.map(d => ({ instanceId: d.hostId, instanceName: d.hostName, data: d.data }))}
                                                    yAxisLabel="Disk I/O (%)"
                                                    valueSuffix="%"
                                                    chartType="spline"
                                                    applyPeriod={applyPeriod}
                                                />
                                            )}
                                            {loadingHostCharts === DATA_LOADED && ioTimeSeries.length === 0 && (
                                                <div className="text-center text-muted p-3">No disk I/O utilisation data available for hosts in this group.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                </div>

            </div>
    );
};

export default GroupDetailSlideout;
